import { AxiosResultDefaultError } from '../request';

/**
 * Utilidades para leer QUÉ dijo realmente un 400 del backend.
 *
 * Varias capas de requests traducían **cualquier** `HTTP_ERROR` con status 400 a
 * `NOT_UNIQUE_CODE`, así que un período con las fechas invertidas, o un nombre
 * demasiado largo, se le mostraba al usuario como "el código ya se encuentra en
 * uso" — sobre un código que muchas veces ni siquiera estaba repetido.
 */

/**
 * Nest devuelve dos formas distintas de 400 y se distinguen por el tipo de
 * `message`:
 *  - **array**  → errores de campo del ValidationPipe (los DTOs).
 *  - **string** → `BadRequestException('CODIGO')` lanzada por un service.
 */
export const parseBadRequestBody = (error: AxiosResultDefaultError) => {
  const message = (error as any)?.error?.response?.data?.message;
  return {
    fieldErrors: Array.isArray(message) ? (message as string[]) : [],
    businessCode: typeof message === 'string' ? message : '',
  };
};

export const isBadRequest = (error: AxiosResultDefaultError): boolean =>
  error.code === 'HTTP_ERROR' && error.status === 400;

/**
 * El código duplicado llega de DOS formas segun quién lo detecte:
 *  - `@UniqueValue(Entidad)` en el DTO → "the code XXX is already in use"
 *  - `schedules.service` → la excepción de negocio `code_in_use`
 */
export const isCodeAlreadyInUse = (error: AxiosResultDefaultError): boolean => {
  if (!isBadRequest(error)) {
    return false;
  }
  const { fieldErrors, businessCode } = parseBadRequestBody(error);
  return (
    businessCode === 'code_in_use' ||
    // El defaultMessage de UniqueValueRule es "the <campo> <valor> is already in
    // use"; se acota a `code` para no confundirlo con otro campo único.
    fieldErrors.some((m) => /^the code .* is already in use$/.test(m)) ||
    fieldErrors.some((m) => m.includes('code in use'))
  );
};

/**
 * `@IsBeforeDate('endDate')` sobre `startDate`; su `defaultMessage` es
 * "startDate must be less than endDate".
 */
export const isStartDateAfterEndDate = (
  error: AxiosResultDefaultError,
): boolean => {
  if (!isBadRequest(error)) {
    return false;
  }
  const { fieldErrors } = parseBadRequestBody(error);
  return fieldErrors.some(
    (m) => m.startsWith('startDate') && m.includes('must be less than'),
  );
};
